import { Injectable } from '@angular/core';
import { ShowIfHttpError } from '@certemy/common';
import { Observable } from 'rxjs';
import { HttpService } from 'services/http/http.service';
import { createQueryParam, buildQueryParams } from 'utils/helpers';
import {
  DataDeliveryDocument,
  DataDeliveryDocumentData,
  IdNameType,
  DataDeliveryLog,
  DataDeliveryDownloadFiles,
  DataDeliveryDownloadCheck,
  DataDeliveryDocumentDataWithLastRun,
  DataDeliveryProgressStatus,
} from './data-delivery.interfaces';
import { FilterOperator, PaginatedResponse, StoredFile } from 'utils/interfaces';

@Injectable()
export class DataDeliveryService {
  _withoutErrorRedirect: boolean = false;

  constructor(private http: HttpService) {}

  get withoutErrorRedirect(): boolean {
    return this._withoutErrorRedirect;
  }

  set withoutErrorRedirect(flag: boolean) {
    this._withoutErrorRedirect = flag;
  }

  @ShowIfHttpError('Cannot receive configured data delivery organizations.')
  getDataDeliveryOrganizations({ limit = 10, page = 1, search = null }): Observable<IdNameType[]> {
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('page', page),
      createQueryParam('search', search),
    ];
    const queryParams = buildQueryParams(params);
    return this.http.get(`/data-delivery/organizations${queryParams}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot receive configured data delivery jobs.')
  getDataDeliveryJobs({
    limit = 10,
    page = 1,
    search = null,
    filter = null,
  }): Observable<PaginatedResponse<DataDeliveryDocument>> {
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('page', page),
      createQueryParam('search', search),
    ];
    if (filter) {
      params.push(
        ...Object.keys(filter).map((key) => createQueryParam(`filter.${key}`, `${FilterOperator.IN}:${filter[key]}`)),
      );
    }
    const queryParams = buildQueryParams(params);
    return this.http.get(`/data-delivery${queryParams}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot receive configured data delivery configuration with last run details.')
  getDataDeliveryConfiguration(id: string): Observable<DataDeliveryDocumentDataWithLastRun> {
    return this.http.get(`/data-delivery/${id}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot receive configured data delivery status with last run details.')
  getDataDeliveryStatus(id: string): Observable<DataDeliveryProgressStatus> {
    return this.http.get(`/data-delivery/${id}/status`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot save data delivery configuration.')
  saveDataDeliveryConfiguration(data: DataDeliveryDocumentData): Observable<DataDeliveryDocumentDataWithLastRun> {
    return this.http.post(`/data-delivery`, data, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot update data delivery configuration.')
  updateDataDeliveryConfiguration(
    key: string,
    data: DataDeliveryDocumentData,
  ): Observable<DataDeliveryDocumentDataWithLastRun> {
    return this.http.put(`/data-delivery/${key}`, data, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot enable data delivery configuration.')
  enableDataDeliveryConfiguration(key: string): Observable<DataDeliveryDocumentDataWithLastRun> {
    return this.http.patch(`/data-delivery/${key}/enable`, null, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot receive data delivery log.')
  getDataDeliveryLog(
    { limit = 10, page = 1, search = null, filter = null },
    dataDeliveryKey: string,
  ): Observable<PaginatedResponse<DataDeliveryLog>> {
    const params = [
      createQueryParam('limit', limit),
      createQueryParam('page', page),
      createQueryParam('search', search),
    ];
    if (filter) {
      params.push(
        ...Object.keys(filter).map((key) => {
          let value = filter[key];
          let operator = FilterOperator.IN;
          if (Array.isArray(filter[key])) {
            value = filter[key][0];
            operator = filter[key][1];
          }

          return createQueryParam(`filter.${key}`, `${operator}:${value}`);
        }),
      );
    }
    const queryParams = buildQueryParams(params);
    return this.http.get(`/data-delivery/${dataDeliveryKey}/logs${queryParams}`, {
      withoutErrorRedirect: this._withoutErrorRedirect,
    });
  }

  @ShowIfHttpError('Cannot receive data delivery download.')
  getDataDeliveryDownloadCheck(id: string): Observable<DataDeliveryDownloadCheck> {
    return this.http.get(`/data-delivery/download/check?id=${id}`);
  }

  @ShowIfHttpError('Cannot receive data delivery files.')
  getDataDeliveryDownloadFiles(id: string, flow: string, token: string): Observable<DataDeliveryDownloadFiles> {
    return this.http.get(`/data-delivery/download/files?id=${id}&flow=${flow}&token=${token}`);
  }
}
